import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2, Shield, User as UserIcon } from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'
import type { User } from '@/types'

export default function Users() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'MARKETING',
  })

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/v1/users/')
      setUsers(response.data)
    } catch (error) {
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingUser) {
        await api.put(`/api/v1/users/${editingUser.id}`, {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          ...(formData.password && { password: formData.password }),
        })
        toast.success('User updated')
      } else {
        await api.post('/api/v1/users/', formData)
        toast.success('User created')
      }
      setShowModal(false)
      setEditingUser(null)
      setFormData({ email: '', full_name: '', password: '', role: 'MARKETING' })
      fetchUsers()
    } catch (error: any) {
      const detail = error.response?.data?.detail
      // Handle FastAPI validation errors (array of objects)
      if (Array.isArray(detail)) {
        toast.error(detail.map((e: any) => e.msg).join(', '))
      } else {
        toast.error(detail || 'Failed to save user')
      }
    }
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)
    setFormData({
      email: user.email,
      full_name: user.fullName,
      password: '',
      role: user.role,
    })
    setShowModal(true)
  }

  const handleDelete = async (user: User) => {
    if (!confirm(`Delete user ${user.fullName}?`)) return
    try {
      await api.delete(`/api/v1/users/${user.id}`)
      toast.success('User deleted')
      fetchUsers()
    } catch (error: any) {
      const detail = error.response?.data?.detail
      if (Array.isArray(detail)) {
        toast.error(detail.map((e: any) => e.msg).join(', '))
      } else {
        toast.error(detail || 'Failed to delete user')
      }
    }
  }

  const getRoleBadge = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'bg-red-500/20 text-red-300 border border-red-500/30',
      marketing: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
      sales: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
    }
    return colors[role.toLowerCase()] || 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
  }

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Users</h1>
        <button
          onClick={() => {
            setEditingUser(null)
            setFormData({ email: '', full_name: '', password: '', role: 'MARKETING' })
            setShowModal(true)
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      <div className="card overflow-hidden p-0">
        <table className="min-w-full">
          <thead className="table-header">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">User</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Role</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Last Login</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="table-row">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-slate-700/50 flex items-center justify-center">
                      {user.role === 'ADMIN' ? (
                        <Shield className="w-5 h-5 text-blue-400" />
                      ) : (
                        <UserIcon className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-slate-200">{user.fullName}</div>
                      <div className="text-sm text-slate-500">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getRoleBadge(user.role)}`}>
                    {user.role.toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    user.isActive ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                  }`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                  {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button onClick={() => handleEdit(user)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-slate-800/50 transition-colors mr-1">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(user)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-800/50 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-slate-100 mb-4">
              {editingUser ? 'Edit User' : 'Add User'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Password {editingUser && '(leave blank to keep current)'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input"
                  {...(!editingUser && { required: true })}
                  minLength={8}
                />
              </div>
              <div>
                <label className="label">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="input"
                >
                  <option value="ADMIN">Admin</option>
                  <option value="MARKETING">Marketing</option>
                  <option value="SALES">Sales</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
